import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROWS_PER_PAGE_LIST } from '../../../_constants/Const';
import { ADD_BUTTON_SIZE, TABLE_HEADER } from '../../../_constants/styleConst';
import { RootState } from '../../../stores/store';
import JsonView from '@uiw/react-json-view';
import { useSelector } from 'react-redux';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
} from '../../../_constants/inspectionConst';
import { formatDate } from '../../../_utils/utils';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { HistorySearchParam } from '../../../stores/history/api';

const OperationHistoryTableColumns = [
  { id: 0, width: '15%', label: 'LBL_SERIAL_NO' },
  { id: 1, width: '15%', label: 'LBL_RMA_NO' },
  { id: 2, width: '15%', label: 'LBL_REGISTRATION_DATE_TIME' },
  { id: 3, width: '15%', label: 'LBL_ROLE_USER' },
  { id: 4, width: '15%', label: 'LBL_STATUS' },
  { id: 5, width: '15%', label: 'LBL_TYPE' },
  { id: 6, width: '10%', label: 'LBL_HISTORY_ITEM' },
] as const;

type HistoryTableProps = {
  params: HistorySearchParam;
  updateParams: (params: HistorySearchParam) => void;
  page: number;
  updatePage: (page: number) => void;
  handleSearch: (requestParams: HistorySearchParam | undefined) => void;
};

const HistoryTable: React.FC<HistoryTableProps> = ({
  params,
  updateParams,
  page,
  updatePage,
  handleSearch,
}) => {
  const { t } = useTranslation();

  const { userData } = useSelector((state: RootState) => state.userInfo);

  /** 操作履歴 */
  const { historyData } = useSelector((state: RootState) => state.history);

  /** 操作履歴の詳細情報 */
  const [detailData, setDetailData] = useState('');

  /** ステータスの表示ラベル */
  const getStatusLabel = (id: number): string => {
    const status = Object.values(HISTORY_STATUSES).find(
      (item) => item.id === id,
    );
    return t(status?.key ?? '');
  };

  /** タイプの表示ラベル */
  const getTypeLabel = (id: number): string => {
    const type = Object.values(HISTORY_TYPE).find((item) => item.id === id);
    return t(type?.key ?? '');
  };

  /** 詳細情報のダイアログ */
  const [isOpenDetailDialog, setIsOpenDetailDialog] = useState<boolean>(false);

  /** ダイアログで詳細情報を表示 */
  const handleDisplayDetail = (data: string) => {
    setDetailData(data);
    setIsOpenDetailDialog(true);
  };

  const convertJsonString = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return { error: e };
    }
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    updatePage(newPage);
    handleSearch({ ...params, rowFrom: newPage * params.perPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updatePage(0);
    updateParams({ ...params, perPage: Number(event.target.value) });
    handleSearch({
      ...params,
      rowFrom: 0,
      perPage: Number(event.target.value),
    });
  };

  return (
    <Box ml="2%" mr="2%" mb={`calc(${ADD_BUTTON_SIZE} * 1.5)`}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {[...OperationHistoryTableColumns]
                .sort((a, b) => a.id - b.id)
                .map((column, i) => (
                  <TableCell
                    key={`history_header_${i}`}
                    sx={{
                      ...TABLE_HEADER,
                      width: column.width,
                      cursor: 'default',
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={
                        column.label === 'LBL_HISTORY_ITEM'
                          ? 'center'
                          : undefined
                      }
                    >
                      {t(column.label)}
                    </Box>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {historyData.operationHistoryList.map((record, i) => (
              <TableRow key={`history_${i}`} hover>
                {/* シリアル番号 */}
                <TableCell>{record.serialNo}</TableCell>
                {/* 修理番号 */}
                <TableCell>{record.rmaNo}</TableCell>
                {/* 登録日(時刻まで) */}
                <TableCell>
                  {formatDate(record.time, userData.dateFormat, true)}
                </TableCell>
                {/* ユーザID */}
                <TableCell>{record.userId}</TableCell>
                {/* 履歴を残す時の修理依頼のステータス */}
                <TableCell>{getStatusLabel(Number(record.status))}</TableCell>
                {/* 履歴を残す操作 */}
                <TableCell>{getTypeLabel(Number(record.type))}</TableCell>
                {/* 残すデータのJSON文字列 */}
                <TableCell>
                  {record.item !== '' && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <IconButton size="small">
                        <OpenInNewIcon
                          fontSize="small"
                          onClick={() => handleDisplayDetail(record.item)}
                        />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          labelRowsPerPage={t('LBL_ROWS_PER_PAGE')}
          rowsPerPageOptions={ROWS_PER_PAGE_LIST}
          component="div"
          count={historyData.totalRows}
          rowsPerPage={params.perPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            t('LBL_DISPLAY_DATA_LABEL', { 0: from, 1: to, 2: count })
          }
        />
      </TableContainer>
      {/* 詳細ダイアログ */}
      <Dialog
        open={isOpenDetailDialog}
        onClose={() => setIsOpenDetailDialog(false)}
        slotProps={{
          paper: {
            sx: {
              width: '80vw',
              maxHeight: '80dvh',
              overflow: 'auto',
            },
          },
        }}
        closeAfterTransition={false}
      >
        <DialogContent>
          <JsonView
            value={convertJsonString(detailData)}
            displayDataTypes={false}
            displayObjectSize={false}
            style={{
              fontSize: 15,
              fontFamily: 'Roboto',
              backgroundColor: '#ffffff',
              padding: '1rem',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HistoryTable;
